'use client';

import { useEffect, useRef } from 'react';
import { useAchievementNotifications } from '@/lib/hooks/training/useAchievementNotifications';
import { useUser } from '@/components/shell/AppShellContext';
import type { Rarity } from '@/lib/training/types';
import { showUnifiedToast } from '@/lib/notifications/show-unified-toast';

/** Map rarity to Tailwind color stem for the unified toast */
const RARITY_ACCENT: Record<Rarity, string> = {
  common: 'gray-500',
  uncommon: 'green-500',
  rare: 'blue-500',
  epic: 'purple-500',
  legendary: 'amber-400',
};

export function TrainingAchievementProvider({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const { events } = useAchievementNotifications(user?.id ?? null);
  const shownRef = useRef<{ userId: string | null; ids: Set<string> }>({
    userId: null,
    ids: new Set(),
  });

  useEffect(() => {
    const userId = user?.id ?? null;
    if (shownRef.current.userId !== userId) {
      shownRef.current = { userId, ids: new Set() };
    }
    // latestEvent is an invalid delivery queue: React batches simultaneous
    // realtime inserts, so only the final award used to get a popup. Consume
    // all unseen events in arrival order, once per event ID.
    for (const event of [...events].reverse()) {
      if (event.user_id !== userId || shownRef.current.ids.has(event.id)) continue;
      shownRef.current.ids.add(event.id);

      const metadata = event.metadata || {};
      const title = (metadata.title as string) || 'Achievement Unlocked!';
      const icon = (metadata.icon as string) || '🏆';
      const rarity = (metadata.rarity as Rarity) || 'common';
      const points = (metadata.points as number) || 0;

      showUnifiedToast({
        variant: 'achievement',
        title,
        icon,
        accentColor: RARITY_ACCENT[rarity],
        categoryLabel: 'Achievement Unlocked!',
        description: `${rarity.charAt(0).toUpperCase() + rarity.slice(1)} · +${points} pts`,
        duration: 5000,
        id: `achievement-${event.id}`,
      });
    }
  }, [events, user?.id]);

  return <>{children}</>;
}
